import i18n from "i18next";
import { Plan } from "@dto/Plan";
import { PlanItem } from "@dto/PlanItem";
import { PlanFullDto } from "./apiTypes";
import { User } from "@dto/User";
import { PlanItem as PlanItemDto } from "@dto/PlanItem";
import { Tour } from "@dto/Tour";
import { Place } from "@dto/Place";
import { FeedPost } from "@dto/FeedPost";
import { convertToCamel } from "@lib/convertToCamel";
import { DatePickerDateRange } from "./datePickerReducer";
import { getBaseUrl } from "./services/api";

const cacheBreak = () => {
  return `cache=${new Date().getTime()}`;
};

let _accessToken: string | undefined = undefined;
const setAccessToken = (accessToken: string | undefined) => {
  _accessToken = accessToken;
};

let _otpToken: string | undefined = undefined;
const setOtpToken = (otpToken: string | undefined) => {
  _otpToken = otpToken;
};

const getCsrfToken = (): string => {
  return ""; //$("meta[name=csrf-token]").prop("content");
};

const getUser = async (): Promise<User | undefined> => {
  try {
    const response = await fetch(
      `${VisitWidget.settings.currentClientApiUrl}/users?${cacheBreak()}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${_accessToken}`,
          "X-CSRF-Token": getCsrfToken(),
        },
      },
    );
    if (!response.ok) {
      return;
    }

    const user = await response.json();
    return user as User;
  } catch {
    return;
  }
};

const createAccessToken = async ({
  email,
  password,
  otp,
}: {
  email?: string;
  password?: string;
  otp?: string;
}): Promise<{
  authToken?: string;
  otpToken?: string;
  errors?: string[];
}> => {
  const path = `/clients/${
    VisitWidget.settings.currentClientId
  }/tokens?${cacheBreak()}`;
  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
    {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        otp,
      }),
      headers: {
        ...(_otpToken && { Authorization: `OTP ${_otpToken}` }),
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRF-Token": getCsrfToken(),
        "X-Requested-With": "XMLHttpRequest",
      },
    },
  );

  const responseBody = await response.json();
  if (!response.ok) {
    throw responseBody;
  }

  return {
    authToken: responseBody.auth_token,
    otpToken: responseBody.otp_token,
    errors: responseBody.errors,
  };
};

const resendOtp = async (): Promise<void> => {
  const path = `/clients/${
    VisitWidget.settings.currentClientId
  }/tokens/resend_otp?${cacheBreak()}`;

  await fetch(`${VisitWidget.settings.currentClientApiUrl}${path}`, {
    method: "POST",
    headers: {
      Authorization: `OTP ${_otpToken}`,
      "Content-Type": "application/json; charset=utf-8",
      "X-CSRF-Token": getCsrfToken(),
      "X-Requested-With": "XMLHttpRequest",
    },
  });
};

const listPlanItems = async (planId: number): Promise<PlanItemDto[]> => {
  const path = `/plans/${planId}/plan_items?${cacheBreak()}`;
  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${_accessToken}`,
        "X-CSRF-Token": getCsrfToken(),
      },
    },
  );

  const responseBody = await response.json();

  if (!response.ok) {
    throw responseBody;
  }

  const planItems = convertToCamel(responseBody) as PlanItemDto[];
  return planItems;
};

export const createGuest = async (): Promise<User> => {
  const response = await fetch(
    `/api/clients/${VisitWidget.settings.currentClientId}/users/create_guest`,
    {
      method: "POST",
    },
  );

  const responseBody = await response.json();
  if (!response.ok) {
    throw responseBody;
  }

  return convertToCamel(responseBody) as User;
};

const createUser = async ({
  email,
  name,
  newsletters,
  captcha,
  postalCode,
  planId,
}: {
  email: string;
  name: string;
  newsletters: { id: number }[];
  captcha: string;
  postalCode?: string;
  planId?: string;
}): Promise<User> => {
  const payload = {
    user: {
      email: email,
      name: name,
      locale: i18n.language,
      newsletters,
    },
  } as Record<string, any>;

  if (!!postalCode) {
    payload.user.postal_code = postalCode;
  }
  if (!!planId) {
    payload.plan_id = String(planId);
  }

  payload["g-recaptcha-response"] = captcha;

  const path = `/users?${cacheBreak()}`;
  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "X-CSRF-Token": getCsrfToken(),
        "Content-Type": "application/json; charset=utf-8",
      },
    },
  );
  const responseBody = await response.json();
  if (!response.ok) {
    throw responseBody;
  }

  return convertToCamel(responseBody) as User;
};

const sendMagicLink = async ({ email }: { email: string }): Promise<void> => {
  const baseUrl = getBaseUrl();
  const path = `/users/magic_link?${cacheBreak()}`;
  const response = await fetch(
    `${baseUrl}/api/clients/${VisitWidget.settings.currentClientId}${path}`,
    {
      method: "POST",
      body: JSON.stringify({
        email: email,
      }),
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
    },
  );
  const responseBody = await response.json();
  if (!response.ok) {
    throw responseBody;
  }
};

const loginWithMagicLink = async (
  magicLinkToken: string,
): Promise<{
  authToken: string;
}> => {
  const baseUrl = getBaseUrl();
  const path = `/api/clients/${VisitWidget.settings.currentClientId}/tokens/create_with_magic_link?${cacheBreak()}`;
  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    body: JSON.stringify({
      magic_link_token: magicLinkToken,
    }),
    headers: {
      "Content-Type": "application/json; charset=utf-8",
    },
  });
  const responseBody = await response.json();
  if (!response.ok) {
    throw responseBody;
  }

  return {
    authToken: responseBody.auth_token,
  };
};

const resetPassword = async ({
  email,
  captcha,
}: {
  email: string;
  captcha: string;
}): Promise<void> => {
  const path = `/users/password?${cacheBreak()}`;
  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
    {
      method: "POST",
      body: JSON.stringify({
        email: email,
        "g-recaptcha-response": captcha,
      }),
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
    },
  );
  const responseBody = await response.json();
  if (!response.ok) {
    throw responseBody;
  }
};

interface UpdatePasswordError {
  errors: {
    invalid_token?: string;
    expired?: string;
    unknown?: string;
    too_short?: string;
  };
}

const updatePassword = async ({
  newPassword,
  resetPasswordToken,
}: {
  newPassword: string;
  resetPasswordToken: string;
}): Promise<{
  authToken: string;
}> => {
  const path = `/users/update_password?${cacheBreak()}`;
  const response = await fetch(
    `/api/clients/${VisitWidget.settings.currentClientId}${path}`,
    {
      method: "POST",
      body: JSON.stringify({
        password: newPassword,
        // backend used to handle validation of password matching, but now the frontend will
        password_confirmation: newPassword,
        reset_password_token: resetPasswordToken,
      }),
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
    },
  );
  const responseBody = await response.json();
  if (!response.ok) {
    throw responseBody as UpdatePasswordError;
  }

  return {
    authToken: responseBody.auth_token,
  };
};

const logout = async () => {
  const path = `/users/sign_out?${cacheBreak()}`;
  await fetch(`${VisitWidget.settings.currentClientApiUrl}${path}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      "X-CSRF-Token": getCsrfToken(),
      "X-Requested-With": "XMLHttpRequest",
    },
  });
};

const listSharedPlans = (categoryIds: number[], pageNumber: number = 1) => {
  return listTours("SharedPlan", categoryIds, [], pageNumber);
};

const listPlaces = async (
  categoryIds: number[] = [],
  exclusiveCategoryIds: number[] = [],
  _dates: DatePickerDateRange | null = null,
  pageNumber = 1,
): Promise<Place[]> => {
  const categoryIdsQueryString = encodeURI(
    categoryIds.map((x) => `category_ids[]=${x}`).join("&"),
  );

  const exclusiveCategoryIdsQueryString = encodeURI(
    exclusiveCategoryIds.map((x) => `exclusive_category_ids[]=${x}`).join("&"),
  );

  const path = `/places?page=${pageNumber}&${categoryIdsQueryString}&${exclusiveCategoryIdsQueryString}&${cacheBreak()}`;

  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
    },
  );

  const responseBody = await response.json();
  if (!response.ok) {
    throw convertToCamel(responseBody);
  }

  const places = convertToCamel(responseBody) as Place[];

  return places;
};

const listEvents = async (
  categoryIds: number[] = [],
  exclusiveCategoryIds: number[] = [],
  dates: DatePickerDateRange | null = null,
  pageNumber = 1,
): Promise<Place[]> => {
  const categoryIdsQueryString = encodeURI(
    categoryIds.map((x) => `category_ids[]=${x}`).join("&"),
  );

  const exclusiveCategoryIdsQueryString = encodeURI(
    exclusiveCategoryIds.map((x) => `exclusive_category_ids[]=${x}`).join("&"),
  );

  const fromDate = dates?.startDate ? `&from_date=${dates.startDate}` : "";
  const toDate = dates?.endDate ? `&to_date=${dates.endDate}` : "";

  const path = `/events?page=${pageNumber}&${categoryIdsQueryString}&${exclusiveCategoryIdsQueryString}${fromDate}${toDate}&${cacheBreak()}`;

  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
    },
  );

  const responseBody = await response.json();
  if (!response.ok) {
    throw convertToCamel(responseBody);
  }

  const places = convertToCamel(responseBody) as Place[];

  return places;
};

const sharePlan = async (
  planId: number,
  emailAddresses: string[] = [],
  phoneNumbers: string[] = [],
): Promise<void> => {
  const path = `/plans/${planId}/share?${cacheBreak()}`;
  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
    {
      method: "POST",
      body: JSON.stringify({
        emails: emailAddresses,
        phone_numbers: phoneNumbers,
      }),
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
    },
  );

  if (!response.ok) {
    throw new Error(response.statusText);
  }
  return;
};

const shareItineraryPlanner = async (
  planId: number,
  email: string,
  name: string,
  phone: string,
  message: string,
): Promise<void> => {
  const path = `/plans/${planId}/share_to_itinerary_planner?${cacheBreak()}`;
  const body = {
    plan_user_email: email,
    plan_user_name: name,
    plan_user_phone_number: phone,
    comments: message,
    locale: i18n.language,
  };
  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw new Error(response.statusText);
  }
  return;
};

const shareSharedPlan = async (
  tourId: number,
  emailAddresses: string[],
): Promise<void> => {
  const path = `/tours/${tourId}/share?${cacheBreak()}`;
  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
    {
      method: "POST",
      body: JSON.stringify({
        emails: emailAddresses,
      }),
      headers: {
        Authorization: `Bearer ${_accessToken}`,
        "X-CSRF-Token": getCsrfToken(),
        "Content-Type": "application/json; charset=utf-8",
      },
    },
  );

  if (!response.ok) {
    throw new Error("Backend error.");
  }
  return;
};

const getPlan = async (planId: number): Promise<PlanFullDto> => {
  const path = `/plans/${planId}}?${cacheBreak()}`;

  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${_accessToken}`,
        "X-CSRF-Token": getCsrfToken(),
        "Content-Type": "application/json; charset=utf-8",
      },
    },
  );

  const responseBody = await response.json();
  if (!response.ok) {
    throw responseBody;
  }

  return responseBody;
};

const createPlan = async (): Promise<Plan> => {
  let path = `/plans?${cacheBreak()}`;
  if (VisitWidget.settings.isKioskEnabled) {
    path += `&kiosk=true`;
  }

  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${_accessToken}`,
        "X-CSRF-Token": getCsrfToken(),
        "Content-Type": "application/json; charset=utf-8",
      },
    },
  );

  const responseBody = await response.json();
  if (!response.ok) {
    throw responseBody;
  }

  return responseBody;
};

const addSharedPlan = async (request: {
  shared_plan: {
    categoryIds: string;
    type: "SharedPlan";
    name: string;
    description: string;
    notes_attributes: { body: string }[];
  };
}) => {
  let path = `/shared_plans?${cacheBreak()}`;

  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
    {
      method: "POST",
      body: JSON.stringify(request),
      headers: {
        Authorization: `Bearer ${_accessToken}`,
        "X-CSRF-Token": getCsrfToken(),
        "Content-Type": "application/json; charset=utf-8",
      },
    },
  );

  return response;
};

const adaptPlanDtoToPlan = (plan: PlanFullDto): Plan => {
  return adaptTourOrPlanDtoToPlan(plan, "plan");
};

const adaptTourToPlan = (tour: Tour): Plan => {
  return adaptTourOrPlanDtoToPlan(tour, "tour");
};

const adaptTourOrPlanDtoToPlan = (
  tourOrPlan: Tour | PlanFullDto,
  type: "tour" | "plan",
): Plan => {
  const shareUrl =
    type === "tour"
      ? `${window.location.origin}/tours/${tourOrPlan.id}?access_token=${
          (tourOrPlan as Tour).accessToken
        }`
      : `${window.location.origin}/plan?plan_id=${tourOrPlan.id}`;

  return {
    coverPhotoUrl:
      tourOrPlan.planItems.length > 0
        ? tourOrPlan.planItems[0].plannable.coverPhotoUrl
        : "/assets/default_cover_photo.png",
    id: tourOrPlan.id,
    planItems: tourOrPlan.planItems.map((x) => {
      return {
        coverPhotoUrl: x.plannable.coverPhotoUrl,
        id: x.id,
        title: x.plannable.name,
        address: x.plannable.location?.address ?? "",
        categoryName:
          x.plannable.categoryIds.length > 0
            ? VisitWidget.categories.queries.getCategoryFromId(
                x.plannable.categoryIds[0],
              )?.name
            : "",
        latitude: x.plannable.location?.latitude,
        longitude: x.plannable.location?.longitude,
        notes: x.notes?.length > 0 ? x.notes[0].body : "",
        originalDto: x,
      };
    }) as unknown[],
    title: tourOrPlan.name,
    notes: tourOrPlan.notes?.length > 0 ? tourOrPlan.notes[0].body : "",
    originalDto: tourOrPlan,
    shareUrl: shareUrl,
  };
};

const getFeedPost = async (idOrSlug: number | string): Promise<FeedPost> => {
  const path = `/feed_posts/${idOrSlug}`;

  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
    },
  );

  const responseBody = await response.json();
  if (!response.ok) {
    throw convertToCamel(responseBody);
  }

  const feedPost = convertToCamel(responseBody) as FeedPost;
  feedPost.coverPhotoUrl = feedPost.imageUrl;
  feedPost.name = feedPost.text;

  return feedPost;
};

const getPlaceTours = async (id: number): Promise<Tour[]> => {
  const path = `/clients/${VisitWidget.settings.currentClientId}/tours?page=1&plannable_id=${id}&plannable_type=Place&locale=${i18n.language}`;

  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
    },
  );

  const responseBody = await response.json();
  if (!response.ok) {
    throw responseBody;
  }
  return responseBody;
};

/**
 * Take the large backend payload and map to what the front end can more easily render and act on.
 * In the future originalDto should be removed and any necessary properties added to root PlanItem.
 */
const adaptPlanItems = (planItems: PlanItemDto[]): PlanItem[] => {
  return planItems.map((x) => {
    return {
      coverPhotoUrl: x.plannable.coverPhotoUrl,
      id: x.id,
      title: x.plannable.name,
      address: x.plannable.location?.address,
      categoryName:
        x.plannable.categoryIds.length > 0
          ? VisitWidget.categories.queries.getCategoryFromId(
              x.plannable.categoryIds[0],
            )?.name
          : "",
      latitude: x.plannable.location?.latitude,
      longitude: x.plannable.location?.longitude,
      notes: x.notes?.length > 0 ? x.notes[0].body : "",
      originalDto: x,
    };
  }) as PlanItem[];
};

const requestViaSms = async (
  phoneNumber: string,
): Promise<{ temporarySmsToken: string }> => {
  const response = await fetch(
    `/api/clients/${VisitWidget.settings.currentClientId}/tokens/request_via_sms`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify({
        phone_number: phoneNumber,
      }),
    },
  );

  const result = convertToCamel(await response.json()) as {
    temporarySmsToken: string;
  };

  return result;
};

const createWithSms = async (
  phoneNumber: string,
  temporarySmsToken: string,
  confirmationCode: string,
) => {
  const response = await fetch(
    `/api/clients/${VisitWidget.settings.currentClientId}/tokens/create_with_sms`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify({
        phone_number: phoneNumber,
        temporary_sms_token: temporarySmsToken,
        confirmation_code: confirmationCode,
      }),
    },
  );

  const result = await response.json();
  if (!response.ok) {
    throw result;
  }
  return {
    authToken: result.auth_token,
  };
};

const getDirections = async (tourId: number): Promise<void> => {
  const path = `/clients/${VisitWidget.settings.currentClientId}/tours/${tourId}/directions_data?cache_version=${VisitWidget.settings.toursCacheVersion}`;

  const response = await fetch(
    `${VisitWidget.settings.currentClientApiUrl}${path}`,
  );

  const responseBody = await response.json();
  if (!response.ok) {
    throw convertToCamel(responseBody);
  }

  return convertToCamel(responseBody);
};

export {
  createAccessToken,
  resendOtp,
  getUser,
  createUser,
  logout,
  listPlanItems,
  setAccessToken,
  setOtpToken,
  sendMagicLink,
  loginWithMagicLink,
  resetPassword,
  updatePassword,
  UpdatePasswordError,
  adaptTourToPlan,
  adaptPlanDtoToPlan,
  listSharedPlans,
  shareSharedPlan,
  createPlan,
  getPlan,
  addSharedPlan,
  getPlaceTours,
  adaptPlanItems,
  getDirections,
  getFeedPost,
  listPlaces,
  listEvents,
  requestViaSms,
  createWithSms,
  sharePlan,
  shareItineraryPlanner,
};
